import React, { useState, useMemo, useEffect } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import { ScrollShadow } from './utils/ScrollShadow'
import { FEEDBACK_LABELS, ZONE_ROW, ZONE_BADGE } from './utils/feedbackStyles'
import { stripHtml } from './utils/stripHtml'

/* Minimal CSS for pseudo-class states */
const TABLE_STYLES = `
.ngn-ddt-select:hover,
.ngn-ddt-select:focus {
  outline: 2px solid #007bff;
  outline-offset: 0.5px;
}
`

const SELECT_STYLE = {
  border: '2px solid #9ca3af', backgroundColor: '#fff',
  cursor: 'pointer', borderRadius: '6px', transition: 'all 0.2s ease',
  userSelect: 'none',
}

const ZONE_BASE = {
  display: 'flex', flexDirection: 'column',
  padding: '0.2rem', borderRadius: '8px', width: '100%',
}
const ZONE_CORRECT = { ...ZONE_BASE, border: '2px solid #28a745' }
const ZONE_WRONG = { ...ZONE_BASE, border: '2px solid #d4a017' }

export const NGNDropdownTableQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)

  const initialSelections = useMemo(() => {
    if (!question.answer) return {}
    try {
      return JSON.parse(question.answer)
    } catch {
      return {}
    }
  }, [question.answer])

  const [selections, setSelections] = useState(initialSelections)

  useEffect(() => {
    const selected = Object.keys(JSON.parse(question.answer || '{}')).length
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.answer])

  const handleChange = async (cellKey, optGuid) => {
    if (readonly || isSaving) return

    const updated = { ...selections, [cellKey]: optGuid }
    setSelections(updated)

    const answerJson = JSON.stringify(updated)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })

    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const { correctAnswer } = question
  const showCellFeedback = showFeedback && showAnswer && correctAnswer

  // Build option text lookup per cell
  const optTextByCell = useMemo(() => {
    const map = {}
    const cellOptions = question.data.cellOptions || {}
    for (const [cellKey, opts] of Object.entries(cellOptions)) {
      const cellMap = {}
      for (const opt of opts) {
        cellMap[opt.guid] = opt.text
      }
      map[cellKey] = cellMap
    }
    return map
  }, [question.data.cellOptions])

  return (
    <>
      <FeedbackDisplay question={question} />
      <style>{TABLE_STYLES}</style>
      <div className="position-relative">
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
      <div className="d-flex flex-column align-items-start" style={{ width: '100%' }}>
        <div
          role="heading"
          aria-level={3}
          className="mb-2 text-left"
          dangerouslySetInnerHTML={{ __html: question.text }}
        />

        <ScrollShadow>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th className="bg-light font-weight-bold" dangerouslySetInnerHTML={{ __html: question.data.tableTitle || '' }} />
                {question.data.tableColumns.map(col => (
                  <th key={col.idx} className="bg-light font-weight-bold text-center" dangerouslySetInnerHTML={{ __html: col.title || '' }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {question.data.tableRows.map(row => (
                <tr key={row.idx}>
                  <td className="text-left font-weight-normal align-middle" dangerouslySetInnerHTML={{ __html: row.text || '' }} />
                  {question.data.tableColumns.map(col => {
                    const cellKey = `${row.idx}-${col.idx}`
                    const opts = question.data.cellOptions[cellKey] || []

                    if (showCellFeedback) {
                      const correctOptGuid = correctAnswer[cellKey]
                      const selectedGuid = selections[cellKey]
                      const optTexts = optTextByCell[cellKey] || {}
                      const isCorrectChoice = selectedGuid && selectedGuid === correctOptGuid

                      if (!correctOptGuid && !selectedGuid) {
                        return <td key={cellKey} />
                      }

                      return (
                        <td key={cellKey}>
                          <div style={isCorrectChoice ? ZONE_CORRECT : ZONE_WRONG}>
                            {isCorrectChoice ? (
                              <span style={ZONE_ROW.correct}>
                                <span dangerouslySetInnerHTML={{ __html: optTexts[selectedGuid] || '' }} />
                                <span style={ZONE_BADGE.correct}>{FEEDBACK_LABELS.correct}</span>
                              </span>
                            ) : (
                              <>
                                {selectedGuid && (
                                  <span style={ZONE_ROW.incorrect}>
                                    <span dangerouslySetInnerHTML={{ __html: optTexts[selectedGuid] || '' }} />
                                    <span style={ZONE_BADGE.incorrect}>{FEEDBACK_LABELS.incorrect}</span>
                                  </span>
                                )}
                                {correctOptGuid && (
                                  <span style={ZONE_ROW.missed}>
                                    <span dangerouslySetInnerHTML={{ __html: optTexts[correctOptGuid] || '' }} />
                                    <span style={ZONE_BADGE.missed}>{FEEDBACK_LABELS.missed}</span>
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      )
                    }

                    return (
                      <td key={cellKey}>
                        <select
                          className="ngn-ddt-select"
                          style={SELECT_STYLE}
                          value={selections[cellKey] || ''}
                          disabled={readonly || isSaving}
                          onChange={e => handleChange(cellKey, e.target.value)}
                        >
                          <option value="">Select Option</option>
                          {opts.map(opt => (
                            <option key={opt.guid} value={opt.guid}>{stripHtml(opt.text)}</option>
                          ))}
                        </select>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      </div>
      </TabSplitLayout>
    </>
  )
}
